/**
 * Mis créditos (`/creditos`).
 *
 * Portado de `PortalCliente::creditos()` + `api_creditos` y de
 * `PortalCliente_model::getCreditoStatus`: por cada línea activa, la **línea**, lo
 * **disponible**, el **balance** y los **vales por canjear**, más las facturas
 * pendientes de pago del cliente.
 *
 * Los saldos se calculan en la API con las mismas piezas del panel
 * (`gestion-creditos.controller`), así que lo que ve el cliente y lo que ve el
 * administrador cuadran al céntimo.
 */

import { AlertTriangle, CreditCard, RotateCcw, Search, Wallet } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Buscador } from '@/components/ui/buscador';
import { Button } from '@/components/ui/button';
import { Paginador } from '@/components/ui/paginador';
import { SectionCard } from '@/components/ui/card';
import { EstadoVacio } from '@/components/ui/estado-vacio';
import { Skeleton } from '@/components/ui/skeleton';
import { TituloPagina } from '@/components/ui/titulo-pagina';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { coincideBusqueda } from '@/lib/buscar';
import { ApiError } from '@/lib/http/api-error';
import { formatCurrency, formatDateValue, formatNumber } from '@/lib/utils';

import { usePortalCreditos } from '../portal.hooks';

export function PortalCreditosPage() {
  useDocumentTitle('Portal · Créditos');
  const consulta = usePortalCreditos();
  const [busqueda, setBusqueda] = useState('');

  // Los hooks van antes de los retornos tempranos de carga/error.
  const pendientes = useMemo(
    () => consulta.data?.facturas_pendientes ?? [],
    [consulta.data],
  );

  const pendientesFiltradas = useMemo(
    () =>
      pendientes.filter((factura) =>
        coincideBusqueda(
          [
            factura.documento,
            factura.tipo,
            formatDateValue(factura.fecha),
            formatDateValue(factura.vencimiento),
            formatCurrency(factura.total),
            formatCurrency(factura.saldo),
          ],
          busqueda,
        ),
      ),
    [pendientes, busqueda],
  );

  const paginacion = usePaginacion(pendientesFiltradas, { clave: busqueda });

  if (consulta.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (consulta.error || !consulta.data) {
    return (
      <Alert variant="error" title="No se pudieron cargar tus créditos">
        {consulta.error instanceof ApiError ? consulta.error.message : 'Intenta nuevamente.'}
      </Alert>
    );
  }

  const { lineas } = consulta.data;
  const totalPendiente = pendientesFiltradas.reduce((suma, factura) => suma + factura.saldo, 0);
  const vencidas = pendientesFiltradas.filter((factura) => factura.dias_vencido > 0);

  return (
    <div className="flex flex-col gap-4">
      <TituloPagina
        icono={Wallet}
        titulo="Mis créditos"
        descripcion="Línea autorizada, saldo disponible y lo que tienes pendiente de pago."
      />

      {lineas.length === 0 ? (
        <Alert variant="info" title="Sin líneas de crédito">
          Tu cuenta no tiene líneas de crédito activas. Si crees que es un error, consulta con la
          estación.
        </Alert>
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {lineas.map((linea) => {
            const consumo = linea.linea > 0 ? Math.min(100, Math.round((linea.balance / linea.linea) * 100)) : 0;

            return (
              <SectionCard
                key={linea.id}
                title={linea.producto}
                description={
                  linea.vencimiento
                    ? `Línea ${linea.producto_abreviado} · vence ${formatDateValue(linea.vencimiento)}`
                    : `Línea ${linea.producto_abreviado}`
                }
              >
                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <div className="rounded-control border border-ink-100 px-3 py-2">
                      <p className="text-[11px] font-semibold tracking-wide text-ink-400 uppercase">
                        Línea
                      </p>
                      <p className="text-sm font-semibold tabular-nums text-ink-900">
                        {formatCurrency(linea.linea)}
                      </p>
                    </div>
                    <div className="rounded-control border border-ink-100 px-3 py-2">
                      <p className="text-[11px] font-semibold tracking-wide text-ink-400 uppercase">
                        Disponible
                      </p>
                      <p className="text-sm font-semibold tabular-nums text-emerald-600">
                        {formatCurrency(linea.disponible)}
                      </p>
                    </div>
                    <div className="rounded-control border border-ink-100 px-3 py-2">
                      <p className="text-[11px] font-semibold tracking-wide text-ink-400 uppercase">
                        Balance
                      </p>
                      <p className="text-sm font-semibold tabular-nums text-amber-600">
                        {formatCurrency(linea.balance)}
                      </p>
                    </div>
                    <div className="rounded-control border border-ink-100 px-3 py-2">
                      <p className="text-[11px] font-semibold tracking-wide text-ink-400 uppercase">
                        Por canjear
                      </p>
                      <p className="text-sm font-semibold tabular-nums text-ink-900">
                        {formatCurrency(linea.por_canjear)}
                      </p>
                    </div>
                  </div>

                  {/* Consumo de la línea: barra simple, sin depender del valor absoluto. */}
                  <div className="flex flex-col gap-1.5">
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-ink-100">
                      <div
                        className="h-full rounded-full bg-brand-500"
                        style={{ width: `${consumo}%` }}
                      />
                    </div>
                    <p className="flex items-center gap-1.5 text-xs text-ink-500">
                      <CreditCard className="size-3.5 shrink-0" aria-hidden="true" />
                      {consumo}% de la línea consumida
                      {linea.vales > 0
                        ? ` · ${formatNumber(linea.vales)} vale(s) de consumo sin liquidar`
                        : ''}
                    </p>
                  </div>

                  {linea.descuentos.length > 0 ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs text-ink-500">Descuentos por producto:</span>
                      {linea.descuentos.map((descuento) => (
                        <Badge key={descuento.producto_id} variant="brand">
                          producto {descuento.producto_id} · {formatNumber(descuento.descuento)}%
                        </Badge>
                      ))}
                    </div>
                  ) : null}
                </div>
              </SectionCard>
            );
          })}
        </div>
      )}

      <SectionCard
        title="Facturas pendientes de pago"
        description={
          pendientes.length === 0
            ? 'No tienes documentos pendientes.'
            : `${busqueda.trim() ? `${pendientesFiltradas.length} de ${pendientes.length}` : `${pendientes.length}`} documento(s) · ${formatCurrency(totalPendiente)}${vencidas.length ? ` · ${vencidas.length} vencida(s)` : ''}`
        }
      >
        {pendientes.length === 0 ? (
          <p className="flex items-center gap-2 text-sm text-ink-500">
            <Wallet className="size-4" aria-hidden="true" />
            Estás al día: no hay facturas a crédito pendientes.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <Buscador
              valor={busqueda}
              onChange={setBusqueda}
              ariaLabel="Buscar facturas pendientes"
              placeholder="Buscar documento o tipo…"
            />

            {pendientesFiltradas.length === 0 ? (
              <EstadoVacio
                icon={Search}
                titulo="Sin coincidencias"
                detalle={`No encontramos facturas pendientes para “${busqueda.trim()}”.`}
                accion={
                  <Button
                    variant="outline"
                    size="sm"
                    leftIcon={<RotateCcw className="size-3.5" aria-hidden="true" />}
                    onClick={() => setBusqueda('')}
                  >
                    Quitar búsqueda
                  </Button>
                }
              />
            ) : (
              <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-100 text-left text-[11px] tracking-wide text-ink-400 uppercase">
                  <th className="py-2 font-semibold">Documento</th>
                  <th className="hidden py-2 font-semibold sm:table-cell">Emitido</th>
                  <th className="py-2 font-semibold">Vence</th>
                  <th className="py-2 text-right font-semibold">Total</th>
                  <th className="py-2 text-right font-semibold">Pendiente</th>
                </tr>
              </thead>
              <tbody>
                {paginacion.visibles.map((factura) => (
                  <tr
                    key={factura.id}
                    className="border-b border-ink-50 transition-colors hover:bg-brand-50/40"
                  >
                    <td className="py-2">
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-mono text-xs whitespace-nowrap text-ink-800">
                          {factura.documento}
                        </span>
                        {factura.dias_vencido > 0 ? (
                          <Badge variant="danger">
                            <AlertTriangle className="size-3" aria-hidden="true" />
                            {factura.dias_vencido} día(s)
                          </Badge>
                        ) : (
                          <Badge variant="neutral">{factura.tipo}</Badge>
                        )}
                      </span>
                    </td>
                    <td className="hidden py-2 text-ink-600 sm:table-cell">
                      {formatDateValue(factura.fecha)}
                    </td>
                    <td className="py-2 text-ink-600">{formatDateValue(factura.vencimiento)}</td>
                    <td className="py-2 text-right tabular-nums text-ink-600">
                      {formatCurrency(factura.total)}
                    </td>
                    <td className="py-2 text-right font-semibold tabular-nums text-amber-600">
                      {formatCurrency(factura.saldo)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
            )}
          </div>
        )}

        <Paginador
          {...paginacion}
          etiqueta="documentos"
          className="-mx-5 -mb-5 mt-3 rounded-b-card"
        />
      </SectionCard>
    </div>
  );
}
